import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useQuery } from 'react-apollo';
import * as turf from '@turf/turf';

import { Definition } from '../../../../../data/Definition';
import { GET_FIELDS } from '../../../../../queries';
import { GetFields } from '../../../../../__generated__/types';
import { useStore } from '../../../providers/AppProvider';
import { EntityTimeSeriesResults, Field, FieldFilter, FieldSortOption, TimeSeriesResult, TimeSeriesStatQueryConfig } from '../../../types/types';
import { fetchData, getTimeSeriesStats } from '../../../utils/dwHelpers';
import { SETTING_SEASON_YEAR } from '../../../utils/settings';
import { useGetAllFields } from '../../../api/getAllFields';
import { useLocation } from 'react-router-dom';
import { dataCloudCover } from '../../../api/landscanService';
import _ from 'lodash';

// configuration for data from the dw for sorting fields.
const fieldSortingProps: TimeSeriesStatQueryConfig[] = [{
  title: 'Biomass',
  name: 'productivity_mean',
  property: 'vvic',
  bin: 'date',
  statistic: 'mean',
  summerize: 'avg',
  min: 12000,
  max: undefined,
  transform: (values: number[]): number[] => {
    const scale = 1 / (20000 - 12000);
    return values.map((v: number) => {
      return (v - 12000) * scale * 100;
    });
    //return [scaled.reduce((a, b) => (a + b)) / scaled.length];
  }
}, {
  title: 'Variability',
  name: 'productivity_variance',
  property: 'vvic',
  bin: 'date',
  statistic: 'stdev',
  summerize: 'avg',
  min: 12000,
  max: undefined,
  transform: (values: number[]): number[] => {
    const scale = 1 / (20000 - 12000);
    return values.map((v: number) => {
      return (v) * scale * 100;
    });
    // const scale = 1 / (20000 - 12000);
    // const scaled =  values.map((v: number) => {
    //   return (Math.sqrt(v)-12000) * scale * 100;
    // });
    //console.log("012624 values", values);
    //console.log("FF062024 values", values)
    //const scaled = values.map((v: number) => {
    //  return (Math.sqrt(v));
    //});
    //console.log("012624 scaled", scaled);
    //console.log("012624 scaled.reduce((a, b) => (a + b)) / values.length", scaled.reduce((a, b) => (a + b)) / values.length);
    //return [scaled.reduce((a, b) => (a + b)) / values.length];
  }
}, {
  title: 'Stress',
  name: 'pgi_mean',
  property: 'pgi',
  bin: 'date',
  statistic: 'mean',
  summerize: 'avg',
  min: 100,
  max: 200,
  transform: (values: number[], rows:any, caller?:string): number[] => {
    //console.log("C031924 values", values);
    /* Logic from sparkline component for stress (evaluates to 100 if theres only one value in the values array)
    const maxValue = Math.max(...values);
    console.log("031924 maxValue", maxValue);
    const range  = maxValue - 100;
    console.log("031924 range", range);
    return values.map((v: number) => {
        console.log("031924 v", v);
        console.log("031924 100 - ((maxValue - v) / range * 100)",100 - ((maxValue - v) / range * 100));
        return 100 - ((maxValue - v) / range * 100);
    });
    return values;
    */
    /* Adapted from vvic transform
    const scale = 1 / (200 - 100);
    const scaled = values.map((v: number) => {
      return (v - 100) * scale * 100;
    });
    return [scaled.reduce((a, b) => (a + b)) / scaled.length];
    */
    //console.log("C050424 rows", rows);
    //console.log("C050424 caller", caller);
    const maxValue = Math.max(...values);
    const minValue = 100;
    //console.log("B050424 maxValue", maxValue);
    const range  = maxValue - 100;
    //console.log("B050424 range", range);
    return values.map((v: number) => {
        //console.log("031924 v", v);
        //console.log("C031924 100 - ((maxValue - v) / range * 100)",100 - ((maxValue - v) / range * 100));
        return (maxValue - v) / ( maxValue - minValue ) * 100
        //return 100 - ((maxValue - v) / range * 100);
    });
  }
}
];
console.log("012624 fieldSortingProps", fieldSortingProps);

const defaultStatSorter = (a: Field, b: Field, property: string, order: 'asc' | 'desc') => {
  const aVal = a.stats.hasOwnProperty(property) ? _.get(a.stats, property).values[0] : undefined;
  const bVal = b.stats.hasOwnProperty(property) ? _.get(b.stats, property).values[0] : undefined;

  // -value if first is less than second, 0 if equal, 1 first greater than second
  if (!aVal && !bVal) {
    return a.name > b.name ? 1 : -1;
  }

  if (!aVal || !bVal) {
    if (!aVal) {
      return order === 'asc' ? -1 : 1;
    } else {
      return order === 'asc' ? 1 : -1;
    }
  }

  return order === 'asc' ? aVal - bVal : bVal - aVal;
}

const defaultPropSorter = (a: Field, b: Field, property: string, order: 'asc' | 'desc') => {
  if (_.get(a, property) === _.get(b, property)) {
    return a.id! - b.id!;
  }

  return _.get(a, property) > _.get(b, property) ? 1 : -1;
}

const intializeSortConfig = (configs: TimeSeriesStatQueryConfig[]): FieldSortOption[] => {
  const sortableNames: FieldSortOption[] = [{
    title: 'Name',
    property: 'name',
    order: 'desc',
    sort: defaultPropSorter
  }];

  for (const config of configs) {
    sortableNames.push({
      title: config.title,
      property: config.name,
      order: 'desc',
      sort: defaultStatSorter
    });
  }

  return sortableNames;
}

const initialSorting = intializeSortConfig(fieldSortingProps);

const initialFilters: FieldFilter = {
  grower_id: [],
  farm_id: [],
  crop_id: [],
  year_planted: [],
  has_dsc: false,
  has_dvs: false,
}

const Store = React.createContext<{
  isFieldLoading: boolean,
  fields: Field[],
  filters: FieldFilter,
  isFiltered: boolean
  updateFilter: (filters: FieldFilter) => void,
  resetFilter: () => void,
  sortOptions: FieldSortOption[],
  sortedBy: FieldSortOption,
  updateSortedBy: (sortOption: FieldSortOption) => void,
  updateDate: (date: any) => void,
}>({
  isFieldLoading: true,
  fields: [],
  filters: {} as FieldFilter,
  isFiltered: false,
  updateFilter: (filters: FieldFilter) => { },
  resetFilter: () => { },
  sortOptions: [],
  sortedBy: initialSorting[0],
  updateSortedBy: (sortOption: FieldSortOption) => { },
  updateDate: (date: any) => { },
});

export const useFieldStore = () => React.useContext(Store);

type FieldsProviderProps = {};

const FieldProvider = ({ children }: PropsWithChildren<FieldsProviderProps>) => {
  const { loading: storeLoading, state: storeState, settings } = useStore();

  const { data: fieldData, loading: fieldDataLoading } = useQuery<GetFields>(GET_FIELDS, { fetchPolicy: "no-cache" });

  //useEffect(() => {
  //  setIsFieldLoading(true);
  //}, [fieldDataLoading]);

  const [isFieldLoading, setIsFieldLoading] = useState<boolean>(true);
  const [fields, setFields] = useState<Field[]>([]);
  const [fieldsCopy, setFieldsCopy] = useState<Field[]>([]);
  //const [fieldsIncrementor, setFieldsIncrementor] = useState<number>(0);
  //const [seasonYear, setSeasonYear] = useState<string | null>(null);
  const [filteredFields, setFilteredFields] = useState<Field[]>([]);

  const [isFiltered, setIsFiltered] = useState<boolean>(false);
  const [isPageLoad, setIsPageLoad] = useState<boolean>(true);
  const [page, setPage] = useState<string>("");
  const [filters, setFilters] = useState<FieldFilter>(initialFilters);

  const [sortOptions, setSortOptions] = useState<FieldSortOption[]>(initialSorting);
  const [sortedBy, setSortedBy] = useState<FieldSortOption>(initialSorting[0]);
  const [date, setDate] = useState<any>(null);

  let { data: fieldsApi } = useGetAllFields();

  const location = useLocation();

   /*useEffect(() => {
    if (fieldData && !fieldDataLoading && fields) {
      const processData = async () => {
        console.log("D121124 fieldData", fieldData)
        console.log("D121124 fields", fields)
       const modifiedData = fieldData.landscan_fields.map((field: any) => {
          return {
            ...field,
            extId: field.field_id // Assuming field_id is the value you want to assign to extId
          };
        });
        console.log("Modified Data:", modifiedData);

      };

      processData().then(() => setIsFieldLoading(false));
    }
  }, [fieldData, fieldDataLoading, fields]);*/


  useEffect(() => {
    if (!storeLoading && !fieldDataLoading && fieldData && fieldsApi) {
      //console.log("A041624 fieldData", fieldData)
      //console.log("A041624 fieldsApi", fieldsApi)

      const loadData = async (dtStart: string, dtEnd: string) => {
        console.log("C062024 loadDwData(dtStart, dtEnd)", dtStart, dtEnd)
        console.log("DD121124 fieldData", fieldData)
        console.log("DD121124 fieldsApi", fieldsApi)
        const dwData = await loadDwData(dtStart, dtEnd);
        console.log("DD121124 dwData", dwData)
        // merge the dw data with the fields.
        console.log("C062024 dwData", dwData)
        console.log("071925 dataCloudCover", dataCloudCover)
        const newfields = fieldData.landscan_fields.map((f: any) => {
          const entity = dwData ? dwData.entities.find((e: any) => e.field_ext_id == f.field_id) : null;
          console.log("C062024 entity", entity)
          const stats = entity && dwData && dwData.sortStats ? dwData.sortStats.find((fp: EntityTimeSeriesResults) => fp.entityId == entity.field_id) : null;
          console.log("C062024 stats",stats)
          const objStats = stats ? stats.results.map((item: TimeSeriesResult) => {
            console.log("C062024 item", item)
            return {
              [item.name]: item
            }
          }) : null;
          let _apiData = fieldsApi ? fieldsApi.find((apiData: any) => {
            //console.log("H041624 apiData", apiData)
            //console.log("H041624 f", f)
            return apiData.id === f.field_id
          }): null;
          console.log("A121224 _apiData", _apiData)
          console.log("A121224 entity", _apiData)
          let field_id = _apiData 
              ? _apiData.entityId 
              : entity 
                ? entity.field_id 
                : undefined
          /*const cloudCoverFeature = dataCloudCover.find(
            (feature: any) => feature.field_id === field_id
          );*/
          const cloudCoverFeature = dataCloudCover ? dataCloudCover[field_id] || null : null;
          return {
            id: f.field_id,
            //ext_id: entity ? entity.field_id : undefined,
            //ext_id: f.field_id,
            //ext_id: 647,
            ext_id: field_id,
            name: f.name,
            description: f.description,
            grower_id: f.grower_id,
            farm_id: f.farm_id,
            crop_id: f.crop_id,
            variety_id: f.variety_id,
            year_planted: f.year_planted,
            enable_level_2: _apiData ? _apiData.enable_level_2 : true,
            enable_level_3: _apiData ? _apiData.enable_level_3 : true,
            area: f.area ?? f.acres,
            area_unit_abbrev: f.area_unit_abbrev,
            url: f.url,
            geom: f.geom,
            centroid: turf.centroid(f.geom),
            stats: objStats ? Object.assign({}, ...objStats) : {},
            farm: f.farm,
            cloud_cover: cloudCoverFeature
          } as Field;
        });
        console.log("E072125 newfields", newfields)
        setFieldsCopy(newfields)
        setFields(newfields);
      }

      const selectedYear = settings[SETTING_SEASON_YEAR];
      const dtStart = `${selectedYear}-01-01 12:00:00.00`;
      const dtEnd = `${selectedYear}-12-31 23:59:59.59`;

      loadData(dtStart, dtEnd).then(() => setIsFieldLoading(false));
    }
  }, [fieldData, fieldDataLoading, storeLoading, fieldsApi]);

  useEffect(() => {
    if (fields.length === 0) {
      return;
    }

    const _load = async () => {
      const definition = storeState.definitions.find((d: Definition) => d.name.toLowerCase().includes('field'));
      console.log("A060624 settings,", settings);
      if (!definition) {
        console.error('Unable to load Field definition');
        return;
      }

      const selectedYear = settings[SETTING_SEASON_YEAR];
      /*console.log("A060624 seasonYear,", seasonYear);
      console.log("A060624 selectedYear,", selectedYear);
      if (seasonYear && (selectedYear === seasonYear)) {
        console.error('No season year update');
        return;
      }
      setSeasonYear(selectedYear);*/
      const dtStart = `${selectedYear}-01-01 12:00:00.00`;
      const dtEnd = `${selectedYear}-12-31 23:59:59.59`;
      console.log("A060624 definition, fieldSortingProps, dtStart, dtEnd", definition, fieldSortingProps, dtStart, dtEnd)
      console.log("A071024 _load");
      return getTimeSeriesStats(definition, fieldSortingProps, dtStart, dtEnd);
    }

    // load sorting data for the selected time period
    _load().then((results: EntityTimeSeriesResults[] | undefined) => {
      if (results) {
        console.log("C060424 results", results);
        console.log("C060424 fields", fields);
        const copy = fieldsCopy.map((field: Field) => {
          const stats = results.find((fp: EntityTimeSeriesResults) => fp.entityId == field.ext_id);
          const objStats = stats ? stats.results.map((item: TimeSeriesResult) => {
            return {
              [item.name]: item
            }
          }) : null;

          return {
            ...field,
            stats: objStats ? Object.assign({}, ...objStats) : {}
          }
        });
        console.log("A071024 B copy", copy)
        console.log("A071024 B fields", fields)
        console.log("A071024 B settings", settings)
        setFields(copy);
      }
    });
  }, [fieldsCopy, settings/*, fieldsIncrementor*/]);

  useEffect(() => {
    console.log("A071024 fields changed", fields);
  }, [fields]);

  useEffect(() => {
    //console.log("B071024 fields", fields);
    //console.log("B071024 date", date);
    //dataStress.data.dates.findIndex((date:string) => date.startsWith(selectedDate));
  }, [date]);

  /*useEffect(() => {
    if (fields.length === 0) {
      return;
    }
    console.log("A060624 fields", fields);
    setFieldsIncrementor(fieldsIncrementor + 1);
  }, [fields]);*/

  useEffect(() => {
    console.log("B071024 fields", fields)
    console.log("B071024 date", date);
    const filterFields = applyFilter(fields);
    const sortedFields = applySort(filterFields);
    console.log("A042224 filters", filters)
    console.log("A042224 Current path is:", location.pathname);
    const keywords = ['fields', 'plants', 'soils'];
    const pathSegments = location.pathname.split('/');
    const foundKeyword = pathSegments.find((segment:string) => keywords.includes(segment));
    console.log("A042224 Found keyword:", foundKeyword); 
    if (isPageLoad || foundKeyword !== page){
      setPage(foundKeyword);
      setIsPageLoad(false);
      if(foundKeyword === 'soils'){
        setFilters({
          ...filters,
          has_dsc: true,
          has_dvs: false,
        });
      }
      if(foundKeyword === 'plants'){
        setFilters({
          ...filters,
          has_dsc: false,
          has_dvs: true, 
        });
      }
      if(foundKeyword === 'fields'){
        setFilters({
          ...filters,
          has_dsc: false,
          has_dvs: false,
        });
      }
    }
    setFilteredFields(sortedFields);
    setIsFiltered(hasFilters(filters));
  }, [fields, filters, sortedBy, date]);


  const applyFilter = (fields: Field[]): Field[] => {
    //console.log("E041624 filters", filters)
    const filtered = fields.filter((field: Field, index:number) => {
      //console.log("C041624 field", field);
      //console.log("G041624 field.enable_level_2", field.enable_level_2, index);
      //console.log("G041624 field.enable_level_3", field.enable_level_3, index);
      //console.log("C041624 filters", filters);
      for (let key in filters) {
        //console.log("E041624 key", key)
        //console.log("D041624 filters[key]", filters[key])
        if (key === 'has_dsc'){
          //console.log("F041624 DSC filters[key]", filters[key])
          if (filters[key] && filters[key] === true) {
            //console.log("FF041624 DVS field.enable_level_3", field.enable_level_3)
            if (!field.enable_level_3) {
              return field.enable_level_3;
            }
          }
        }
        if (key === 'has_dvs'){
          //console.log("F041624 DVS filters[key]", filters[key])
          if (filters[key] && filters[key] === true) {
            //console.log("FF041624 DVS field.enable_level_2", field.enable_level_2)
            if (!field.enable_level_2) {
              return field.enable_level_2;
            }
          }
        }
        if (_.get(filters, key) && _.get(filters, key).length > 0 && _.get(filters, key).indexOf(_.get(field, key) ? _.get(field, key).toString() : '') === -1)
          return false;
      }
      return true;
    });

    return [...filtered];
  }

  const applySort = (fields: Field[]): Field[] => {
    //console.log("A060225 applySort => fields", fields);
    //console.log("A060225 applySort => date", date);
    //console.log("A060225 applySort => sortedBy", sortedBy);
    if (!sortedBy) {
      return fields;
    }
    return fields.sort((a: Field, b: Field) => {
      //console.log("AA060225 a,b", a,b);
      let aFarmVal = "";
      let bFarmVal = "";
      let aVal = undefined;
      let aTime = undefined;
      let bVal = undefined;
      let bTime = undefined;
      if  (a.hasOwnProperty("farm")) {
        aFarmVal = a["farm"] && a["farm"].name ? a["farm"].name : "";
      }
      if  (b.hasOwnProperty("farm")) {
        bFarmVal = b["farm"] && b["farm"].name ? b["farm"].name : "";
      }
      if  (a.stats.hasOwnProperty(sortedBy.property)) {
        aVal = a.stats.hasOwnProperty(sortedBy.property) ? _.get(a.stats, [sortedBy.property, 'values'], undefined) : undefined;
        aTime = a.stats.hasOwnProperty(sortedBy.property) ? _.get(a.stats, [sortedBy.property, 'time'], undefined) : undefined;
      }
      if ( b.stats.hasOwnProperty(sortedBy.property)) {
        bVal = b.stats.hasOwnProperty(sortedBy.property) ? _.get(b.stats, [sortedBy.property, 'values'], undefined) : undefined;
        bTime = b.stats.hasOwnProperty(sortedBy.property) ? _.get(b.stats, [sortedBy.property, 'time'], undefined) : undefined;
      }
      if (sortedBy.property === "name"){
        //console.log("AA060225 aVal,bVal", aVal,bVal);
        bVal = `${bFarmVal}${b.name}`;
        aVal = `${aFarmVal}${a.name}`;
        //console.log("AA060225 aVal,bVal", aVal,bVal);
        return sortedBy.order === 'asc' ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
        //return sortedBy.sort(a, b, sortedBy.property, sortedBy.order);
      }
      if (Array.isArray(aVal) && Array.isArray(bVal)) {
        const aIndex = aTime && date
          ? aTime.findIndex((_date:string) => _date.startsWith(date)) 
          : date 
            ? null
            : aVal.length - 1;
        const bIndex = bTime && date
          ? bTime.findIndex((_date:string) => _date.startsWith(date)) 
          : date
            ? null
            : bVal.length - 1;
        //console.log("B071024 bIndex", bIndex);
        //console.log("B071024 aIndex", aIndex);
        const aLastVal = aIndex && aIndex >= 0 ? aVal[aIndex] : -Infinity;
        const bLastVal = bIndex && bIndex >= 0 ? bVal[bIndex] : -Infinity;
        //console.log("B071024 bLastVal", bLastVal);
        //console.log("B071024 bLastVal", bLastVal);
        return sortedBy.order === 'asc' ? aLastVal - bLastVal : bLastVal - aLastVal;
      }
      return sortedBy.sort(a, b, sortedBy.property, sortedBy.order);
    })
  }


  const hasFilters = (filter: FieldFilter): boolean => {
    for (let key in filter) {
      if (Array.isArray(_.get(filter, key)) && _.get(filter, key).length) {
        return true;
      }
    }

    return false;
  }


  const updateFilter = (filters: FieldFilter) => {
    console.log("E041624 filters", filters);
    setFilters(filters);
  }


  const resetFilter = () => {
    console.log("E041624 initialFilters", initialFilters);
    setFilters(initialFilters);
  }

  const updateSortedBy = (sortOption: FieldSortOption) => {
    setSortedBy(sortOption);
  }

  const updateDate = (date: any) => {
    setDate(date);
  }


  const loadDwData = async (dtStart: string, dtEnd: string) => { 
    const definition = storeState.definitions.find((d: Definition) => d.name.toLowerCase().includes('field'));
    if (!definition) {
      console.error('Unable to find field definition');
      return null;
    }

    try {
      // load all fields from dw that gives the relationship from landscan fields to dw entities
      const fieldDwEntitiesPromise = fetchData('/api/entities', { "definitionId": definition.id });

      // load data for fields used in sorting
      console.log("definition, fieldSortingProps, dtStart, dtEnd",definition, fieldSortingProps, dtStart, dtEnd)
      const fieldSortStatsPromise = getTimeSeriesStats(definition, fieldSortingProps, dtStart, dtEnd);

      const fieldDwEntities = await fieldDwEntitiesPromise;
      const fieldSortStats = await fieldSortStatsPromise;

      return {
        entities: fieldDwEntities,
        sortStats: fieldSortStats
      };
    } catch (e) {
      console.error('dw field data load', e);
      return null;
    }
  }


  return (
    <Store.Provider value={{
      isFieldLoading,
      fields: filteredFields,
      isFiltered,
      filters,
      updateFilter,
      resetFilter,
      sortOptions,
      sortedBy,
      updateSortedBy, 
      updateDate
    }}>
      {children}
    </Store.Provider>
  );
};

export default FieldProvider;
